import React, { memo, useCallback, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useForceUpdate } from "@hooks/useForceUpdate";
import { useNotification } from "@hooks/useNotification";
import { useUserPermission } from "@hooks/users";
import { Controller, useForm } from "react-hook-form";

import IconInfo from "@image/icones/DocumentsEmployers/iconInfo.svg";

import { ListboxSimple } from "@components/tailwindComponents";
import BlackTooltip from "@components/tooltip/BlackTooltip";

import { setNotificationWarning } from "@helpers/_functions";
import { Employer, Users } from "@services/all.services";

const STORAGE_USER_ID_KEY = "userId";

const UserActivity = {
  ACTIVE: "Connecté",
  DESACTIVE: "Déconnecté",
  INVITATION_SENT: "Invitation envoyée",
  INVITATION_EXPIRED: "Invitation expirée"
};

const translateStatusOption = (translate, id, testId) => ({
  _id: id,
  name: translate(id),
  testId
});

const AccessRenderComponent = ({ rowData }) => {
  const { t: translate } = useTranslation("pages.employés.listEmployés");
  const {
    dispatch,
    showSuccess,
    showError,
    notificationTranslator,
    showWarning
  } = useNotification();
  const forceUpdate = useForceUpdate();
  // @ts-expect-error
  const { canToggleAlteeAccess } = useUserPermission();
  const currentUserId = localStorage.getItem(STORAGE_USER_ID_KEY);
  const isCurrentUser = rowData._id === currentUserId;
  const isInvitationSent =
    rowData.statusActivity === UserActivity.INVITATION_SENT;
  const isInvitationExpired =
    rowData.statusActivity === UserActivity.INVITATION_EXPIRED;
  const hasExpiredInvitationAccess =
    rowData.statusActivity === UserActivity.ACTIVE &&
    rowData.infoActivity?.isAlreadyLogged === false;

  const { control, setValue } = useForm({
    defaultValues: {
      status: rowData.statusActivity
    }
  });

  const accessOptions = useMemo(
    () => [
      translateStatusOption(translate, UserActivity.ACTIVE, "activated"),
      translateStatusOption(translate, UserActivity.DESACTIVE, "desactivated")
    ],
    [translate]
  );

  const optionSent = useMemo(
    () =>
      translateStatusOption(
        translate,
        UserActivity.INVITATION_SENT,
        "invitation_sent"
      ),
    [translate]
  );

  const optionExpired = useMemo(
    () =>
      translateStatusOption(
        translate,
        UserActivity.INVITATION_EXPIRED,
        "expired_invitation"
      ),
    [translate]
  );

  const optionResend = useMemo(
    () => ({
      _id: "resend_invitation",
      name: translate("Renvoyer l'invitation"),
      testId: "resend_invitation"
    }),
    [translate]
  );

  const optionList = useMemo(() => {
    if (hasExpiredInvitationAccess || isInvitationExpired) {
      return [...accessOptions, optionResend];
    }

    return accessOptions;
  }, [
    accessOptions,
    hasExpiredInvitationAccess,
    isInvitationExpired,
    optionResend
  ]);

  const getSelectedOption = useCallback(
    (statusId) => {
      if (statusId === UserActivity.INVITATION_SENT) {
        return optionSent;
      }

      if (
        statusId === UserActivity.INVITATION_EXPIRED ||
        (statusId === UserActivity.ACTIVE && hasExpiredInvitationAccess)
      ) {
        return optionExpired;
      }

      return optionList.find((option) => option._id === statusId) || {};
    },
    [hasExpiredInvitationAccess, optionExpired, optionList, optionSent]
  );

  useEffect(() => {
    setValue("status", rowData.statusActivity);
  }, [rowData.statusActivity]);

  const handleStatusChange = useCallback(
    async (accessSelected, onChange) => {
      const selectedStatus = accessSelected?._id;
      if (!selectedStatus) {
        return;
      }

      if (
        (isInvitationSent || isInvitationExpired) &&
        selectedStatus === UserActivity.ACTIVE
      ) {
        return;
      }

      if (selectedStatus === "resend_invitation") {
        try {
          await Users.loginInvite(rowData._id);
          showSuccess("invitation_sent");
          onChange(selectedStatus);
        } catch (error) {
          console.log(error);
          showError("error");
        }
        return;
      }

      if (
        [UserActivity.ACTIVE, UserActivity.DESACTIVE].includes(
          selectedStatus
        ) &&
        rowData.statusActivity !== selectedStatus
      ) {
        const isDesactivated = selectedStatus === UserActivity.DESACTIVE;

        try {
          const response = await Employer.updateById({
            _id: rowData._id,
            desactivated: isDesactivated,
            activationFunction: true,
            alreadyActivated: rowData.reactivated,
            username: rowData.personnalInformation?.name,
            email: rowData.email,
            language: rowData.language,
            enterpriseId: localStorage.getItem("enterpriseId")
          });

          if (response.status === 200) {
            rowData.desactivated = isDesactivated;
            forceUpdate();

            if (isDesactivated) {
              showSuccess(notificationTranslator("desactivation employed"));
            } else if (rowData.reactivated && !rowData.new) {
              const warningMessage = (
                <>
                  {notificationTranslator("reactivation part 1")}
                  <br />
                  <br />
                  <strong>ATTENTION:</strong>{" "}
                  {notificationTranslator("reactivation part 2")}
                </>
              );
              setNotificationWarning(dispatch, warningMessage);
            } else {
              showSuccess(notificationTranslator("activation employed"));
            }

            onChange(selectedStatus);
          }
        } catch (error) {
          console.log(error);
          showError("error");
        }
        return;
      }

      onChange(selectedStatus);
    },
    [
      dispatch,
      forceUpdate,
      isInvitationExpired,
      isInvitationSent,
      notificationTranslator,
      rowData,
      showError,
      showSuccess
    ]
  );

  const isDisabled =
    isInvitationSent ||
    isInvitationExpired ||
    !canToggleAlteeAccess ||
    isCurrentUser;

  const handleContainerClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (!canToggleAlteeAccess) {
        showWarning(
          "Action indisponible, veuillez demander l'accès à votre administrateur"
        );
      }
    },
    [canToggleAlteeAccess, showWarning]
  );

  return (
    <div onClick={handleContainerClick}>
      <Controller
        control={control}
        name="status"
        render={({ field: { onChange, value } }) => (
          <ListboxSimple
            placeholder={translate("Statut")}
            name="status"
            optionList={optionList}
            selectedValue={getSelectedOption(value)}
            setSelectedValue={(option) => handleStatusChange(option, onChange)}
            noRadio
            invalid={rowData.desactivated}
            readOnly={isCurrentUser}
            customClass="!arh-ml-0"
            disabled={isDisabled}
            noOpen={!canToggleAlteeAccess}
          />
        )}
      />
    </div>
  );
};

export const AccessRender = memo(AccessRenderComponent);

const HeaderAccessRenderComponent = ({ headerData }) => {
  const { t: translate } = useTranslation("pages.employés.listEmployés");

  const tooltipContent = useMemo(
    () => (
      <div className="arh-flex arh-max-w-[20.188rem] arh-flex-col arh-gap-4 arh-p-1">
        <div className="arh-w-full">
          <div className="arh-text-[11px] arh-font-medium arh-underline">
            {translate("Attention")}:
          </div>
          <div className="arh-whitespace-pre-wrap arh-text-[11px] arh-font-medium">
            {translate(
              "Les utilisateurs sans accès ne pourront pas utiliser les fonctionnalités collaboratives d’Altee"
            )}
            .
          </div>
        </div>
        <div className="arh-w-full">
          <div className="arh-text-[11px] arh-font-medium arh-underline">
            {translate("Note")}:
          </div>
          <div className="arh-whitespace-pre-wrap arh-text-[11px] arh-font-medium">
            {translate(
              "Il est possible d’activer en un clic l’accès à Altee pour tous les employés en cliquant dans le menu ci-dessus avec les trois points verticaux"
            )}
            .
          </div>
        </div>
      </div>
    ),
    [translate]
  );

  return (
    <div className="arh-flex arh-w-full arh-items-center arh-justify-between">
      <span className="arh-text-xs arh-font-semibold arh-text-darkColor">
        {translate("Accès personnel")}
      </span>
      <BlackTooltip
        title={tooltipContent}
        placement="top"
        arrow
        enterDelay={0}
        leaveDelay={0}
        disableFocusListener
        disableTouchListener
      >
        <img
          src={IconInfo}
          alt=""
          className="info_content"
        />
      </BlackTooltip>
    </div>
  );
};

export const HeaderAccessRender = memo(HeaderAccessRenderComponent);
